import { useLayoutEffect, useRef, useState } from 'react';
import { createUpload, orgID, type UploadStage } from '@yingbaba/api';
import { Button, Notice, compactFileSize } from '@yingbaba/ui';
import { useUnsavedChanges } from './UnsavedChanges';

type Job = {
  id: string;
  name: string;
  size: number;
  status: 'waiting' | UploadStage | 'done' | 'failed';
  error?: string;
  upload?: ReturnType<typeof createUpload>;
};
const labels = {
  waiting: '等待上传',
  preparing: '准备上传',
  uploading: '正在上传',
  confirming: '正在保存',
  done: '已保存',
  failed: '未完成',
};
export function useProjectUploads(projectID: string, write: boolean, onUploaded: () => void) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectionError, setSelectionError] = useState('');
  const latest = useRef<Job[]>([]);
  const running = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  const generation = useRef(0);
  const uploaded = useRef(onUploaded);
  uploaded.current = onUploaded;
  function update(next: Job[]) {
    latest.current = next;
    if (alive.current) setJobs(next);
  }
  function clear() {
    generation.current++;
    latest.current.forEach((job) => job.upload?.cancel());
    running.current = false;
    update([]);
  }
  const organization = orgID();
  useLayoutEffect(() => {
    alive.current = true;
    setJobs([]);
    setSelectionError('');
    return () => {
      alive.current = false;
      clear();
    };
  }, [projectID, organization]);
  useLayoutEffect(() => {
    if (!write) clear();
  }, [write]);
  const unfinished = jobs.some((job) => job.status !== 'done');
  useUnsavedChanges({
    dirty: unfinished,
    title: '仍有文件未上传完成',
    description: '已保存的文件会保留。离开后，未完成的文件需要重新选择上传。',
    discardLabel: '离开并清除队列',
    continueLabel: '留在本页',
    discard: clear,
  });
  async function process() {
    if (running.current || !write) return;
    running.current = true;
    const current = generation.current;
    let saved = false;
    try {
      for (;;) {
        const job = latest.current.find((entry) => entry.status === 'waiting');
        if (!job || current !== generation.current || !alive.current) break;
        const patch = (value: Partial<Job>) => {
          if (current === generation.current && alive.current)
            update(
              latest.current.map((entry) => (entry.id === job.id ? { ...entry, ...value } : entry)),
            );
        };
        try {
          await job.upload!.run((stage) => patch({ status: stage }));
          patch({ status: 'done', error: undefined, upload: undefined });
          saved = true;
        } catch (error) {
          patch({
            status: 'failed',
            error: error instanceof Error ? error.message : '上传未完成，请重试',
          });
        }
      }
    } finally {
      if (current === generation.current) {
        running.current = false;
        if (saved && alive.current) uploaded.current();
      }
    }
  }
  function add(files: File[]) {
    if (!write || !files.length) return;
    if (files.length > 20) {
      setSelectionError('每次最多选择 20 个文件，请减少选择数量');
      return;
    }
    setSelectionError('');
    // Completed rows need no File references; discard them when starting another batch.
    const next: Job[] = files.map((file) => {
      const invalid =
        file.size === 0
          ? '文件为空，请重新选择'
          : file.size > 100 * 1024 * 1024
            ? '单个文件最大 100 MB'
            : '';
      const form = new FormData();
      form.append('file', file);
      return {
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        status: invalid ? 'failed' : 'waiting',
        error: invalid || undefined,
        upload: invalid ? undefined : createUpload(`/projects/${projectID}/files`, form),
      };
    });
    update([...latest.current.filter((job) => job.status !== 'done'), ...next]);
    void process();
  }
  return {
    choose: () => input.current?.click(),
    input: (
      <input
        type="file"
        multiple
        ref={input}
        className="sr-only"
        aria-label="选择项目文件"
        disabled={!write}
        onChange={(event) => {
          add(Array.from(event.target.files || []));
          event.target.value = '';
        }}
      />
    ),
    queue: (jobs.length > 0 || !!selectionError) && (
      <section className="project-upload-queue" aria-label="文件上传队列">
        {selectionError && <Notice>{selectionError}</Notice>}
        <div className="row spread wrap">
          <strong className="small" role="status">
            已保存 {jobs.filter((job) => job.status === 'done').length} / {jobs.length} 个文件
          </strong>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => update(latest.current.filter((job) => job.status !== 'done'))}
          >
            清除已完成
          </Button>
        </div>
        <ul>
          {jobs.map((job) => (
            <li key={job.id} className={`project-upload-item is-${job.status}`}>
              <div className="project-upload-info">
                <strong className="small">{job.name}</strong>
                <span className="small muted">
                  {compactFileSize(job.size) || '0 B'} · {labels[job.status]}
                </span>
                {job.error && <span className="small form-error">{job.error}</span>}
              </div>
              {['preparing', 'uploading', 'confirming'].includes(job.status) && (
                <progress aria-label={`${job.name}上传进度`} />
              )}
              {job.status === 'failed' && (
                <div className="row">
                  {job.upload && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        update(
                          latest.current.map((entry) =>
                            entry.id === job.id
                              ? { ...entry, status: 'waiting', error: undefined }
                              : entry,
                          ),
                        );
                        void process();
                      }}
                    >
                      重试
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      job.upload?.cancel();
                      update(latest.current.filter((entry) => entry.id !== job.id));
                    }}
                  >
                    移除
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    ),
  };
}
